import { gql, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { deepCloneAndOmit } from 'src/utils/objects';
import { BehaviorConfig } from './behavior-config.types';

const GET_BEHAVIOR_CONFIG_QUERY = gql`
  query GetBehaviorConfig {
    getBehaviorConfig {
      version
      triggers {
        label
        value
      }
      conditionTypes {
        label
        value
      }
      actionTypes {
        label
        value
      }
      operators {
        text {
          value
          label
          arity
        }
        textarea {
          value
          label
          arity
        }
        list {
          value
          label
          arity
        }
        number {
          value
          label
          arity
        }
        date {
          value
          label
          arity
        }
        boolean {
          value
          label
          arity
        }
      }
      conditions {
        partnerBusinessHours {
          triggers
          attribute
          attributeLabel
          uiType
          operators {
            value
            label
            arity
          }
          options {
            value
            label
          }
          group
        }
        actor {
          triggers
          attributeLabel
          uiType
          attribute
          operators {
            value
            label
            arity
          }
          options {
            value
            label
            __typename
          }
          group
          __typename
        }
        partnerSetting {
          triggers
          attributeLabel
          uiType
          attribute
          operators {
            value
            label
            arity
          }
          options {
            value
            label
          }
          group
        }
        data {
          triggers
          group
          attribute
          attributeLabel
          uiType
          operators {
            value
            label
            arity
          }
          options {
            value
            label
          }
          optionsQuery {
            graphqlQuery
          }
        }
        contactCenterHours {
          triggers
          attribute
          attributeLabel
          uiType
          operators {
            value
            label
            arity
          }
          options {
            value
            label
          }
          group
        }
      }
      actions {
        data {
          triggers
          group
          attribute
          attributeLabel
          uiType
          options {
            value
            label
          }
          optionsQuery {
            graphqlQuery
          }
        }
        sendSlackMessage {
          triggers
          attributes {
            name
            uiType
            label
            placeholder
            options {
              value
              label
            }
          }
        }
        sendConversationMessage {
          triggers
          attributes {
            name
            uiType
            label
            placeholder
          }
        }
        manageConversationLabels {
          triggers
          attributes {
            name
            uiType
            label
            multiple
            optionsQuery {
              graphqlQuery
            }
            options {
              value
              label
            }
          }
        }
        manageSubAssignees {
          triggers
          attributes {
            name
            uiType
            label
            multiple
            optionsQuery {
              graphqlQuery
            }
            options {
              value
              label
            }
          }
        }
        webhook {
          triggers
          attributes {
            name
            uiType
            label
            placeholder
            options {
              value
              label
            }
          }
        }
        signal {
          triggers
          attributes {
            name
            uiType
            label
            dependsOn {
              attribute
              value
            }
            options {
              value
              label
            }
          }
        }
        createTwilioTask {
          triggers
          attributes {
            name
            uiType
            label
            options {
              label
              value
            }
          }
        }
        runTriage {
          triggers
          attributes {
            name
            uiType
            label
            placeholder
          }
        }
      }
    }
  }
`;

interface GetBehaviorConfigQuery {
  getBehaviorConfig: BehaviorConfig & { __typename: string };
}

export const useBehaviorConfig = () => {
  const { user } = useCurrentUserProfile();
  const { data: behaviorConfigData, loading: behaviorConfigLoading } =
    useQuery<GetBehaviorConfigQuery>(GET_BEHAVIOR_CONFIG_QUERY, {
      context: {
        includePlatformLevelData: user?.partnerId === PLATFORM_PARTNER_ID,
      },
    });

  const configWithoutTypenameKeys = useMemo(
    () =>
      behaviorConfigData?.getBehaviorConfig
        ? deepCloneAndOmit<BehaviorConfig & { __typename: string }>(
            behaviorConfigData?.getBehaviorConfig,
            ['__typename'],
          )
        : null,
    [behaviorConfigData?.getBehaviorConfig],
  );

  return {
    behaviorConfig: configWithoutTypenameKeys,
    loading: behaviorConfigLoading,
  };
};
